import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageCircle, ArrowRightLeft, AtSign, Volume2, VolumeX, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { whatsappAPI } from './whatsappAPI';
import { useWaRealtime, getConversaAberta, SONS } from './waRealtime';
import { telefoneConversa } from './organizacao';
import { semMarcas } from './waFormat';

/* Sino do atendimento no topo (todas as telas):
 * - mensagem nova nas conversas da pessoa e na fila que ela pode puxar → som de 2 toques
 * - atendimento transferido para ela (por colega ou distribuição) → som de 3 toques, fica marcado até ela abrir
 * - menção em nota interna → som próprio
 * A conversa aberta na tela de Atendimento não toca nem conta. */

const POLL_MS = 20000;
const nomeConv = (c) => c.cliente_nome || c.push_name || telefoneConversa(c) || 'Contato do WhatsApp';
const lerSom = () => { try { return localStorage.getItem('wa_alerta_som') !== '0'; } catch (_) { return true; } };
const hora = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};
function avisoDesktop(titulo, corpo, tag) {
  try {
    if (document.hidden && typeof window.Notification === 'function' && Notification.permission === 'granted') {
      const n = new Notification(titulo, { body: corpo, icon: '/favicon.ico', tag });
      n.onclick = () => { window.focus(); n.close(); };
      setTimeout(() => n.close(), 8000);
    }
  } catch (_) { /* noop */ }
}

export default function AlertasWA() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [dados, setDados] = useState(null);       // null = ainda não carregou ou módulo indisponível
  const [som, setSom] = useState(lerSom);
  const anterior = useRef(null);                  // { msgs: {jid: nao_lidas}, transf: Set(ids), mencoes }
  const somRef = useRef(som);
  somRef.current = som;
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  const carregarT = useRef(null);

  const carregar = useCallback(async () => {
    let d;
    try { d = (await whatsappAPI.alertas()).data; } catch (e) {
      if ([403, 404].includes(e?.response?.status)) setDados(null);
      return;
    }
    if (!d || !Array.isArray(d.conversas)) return;
    const aberta = getConversaAberta();
    const conversas = d.conversas.filter(c => c.jid !== aberta);
    const transferencias = d.transferencias || [];
    const prev = anterior.current;
    if (prev) {
      const novasT = transferencias.filter(t => !prev.transf.has(t.id));
      const novasM = conversas.filter(c => (c.nao_lidas || 0) > (prev.msgs[c.jid] || 0));
      const naTela = pathRef.current === '/whatsapp';
      if (novasT.length) {
        if (somRef.current) SONS.transferencia();
        const t = novasT[0];
        toast.info(`${t.de || 'Alguém'} transferiu ${t.cliente || 'um atendimento'} para você`, {
          action: { label: 'Abrir', onClick: () => navigate(`/whatsapp?jid=${encodeURIComponent(t.jid)}`) },
        });
        avisoDesktop('🔄 Atendimento transferido para você', t.cliente || '', 'wa-transf');
      } else if ((d.mencoes || 0) > prev.mencoes) {
        if (somRef.current) SONS.mencao();
        if (!naTela) toast.info('Você foi mencionado numa nota do atendimento');
        avisoDesktop('@ Você foi mencionado', 'Nota interna no atendimento', 'wa-mencao');
      } else if (novasM.length) {
        if (somRef.current) SONS.mensagem();
        const c = novasM[0];
        if (!naTela) {
          toast(`💬 ${nomeConv(c)}`, {
            description: semMarcas(c.ultima_mensagem || '').slice(0, 120),
            action: { label: 'Abrir', onClick: () => navigate(`/whatsapp?jid=${encodeURIComponent(c.jid)}`) },
          });
        }
        avisoDesktop(`💬 ${nomeConv(c)}`, semMarcas(c.ultima_mensagem || ''), 'wa-msg');
      }
    }
    anterior.current = {
      msgs: Object.fromEntries(conversas.map(c => [c.jid, c.nao_lidas || 0])),
      transf: new Set(transferencias.map(t => t.id)),
      mencoes: d.mencoes || 0,
    };
    setDados({ conversas, transferencias, mencoes: d.mencoes || 0 });
  }, [navigate]);

  const carregarLogo = useCallback((ms = 400) => {
    clearTimeout(carregarT.current);
    carregarT.current = setTimeout(carregar, ms);
  }, [carregar]);

  useWaRealtime(useCallback((ev) => {
    if (ev.type === 'wa_transferencia' || ev.type === 'wa_mencao') carregarLogo(100);
    else if (ev.type === 'wa_message' && !ev.message?.from_me) carregarLogo(600);
    else if (ev.type === 'wa_conversation') carregarLogo(1200);
  }, [carregarLogo]), true, useCallback((reconexao) => { if (reconexao) carregarLogo(300); }, [carregarLogo]));

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, POLL_MS);
    const atualizar = () => carregarLogo(200);
    window.addEventListener('wa-alertas-atualizar', atualizar);
    return () => { clearInterval(t); clearTimeout(carregarT.current); window.removeEventListener('wa-alertas-atualizar', atualizar); };
  }, [carregar, carregarLogo]);

  // abrir/fechar uma conversa no Atendimento muda o que conta
  useEffect(() => { carregarLogo(800); }, [pathname, carregarLogo]);

  if (!dados) return null;
  const { conversas, transferencias, mencoes } = dados;
  const total = conversas.length + transferencias.length + mencoes;

  const abrir = (jid, transfId) => {
    if (transfId) whatsappAPI.alertasLidas({ ids: [transfId] }).then(() => carregarLogo(100)).catch(() => {});
    navigate(`/whatsapp?jid=${encodeURIComponent(jid)}`);
  };
  const alternarSom = (e) => {
    e.preventDefault();
    const v = !som;
    setSom(v);
    try { localStorage.setItem('wa_alerta_som', v ? '1' : '0'); } catch (_) { /* noop */ }
    if (v) SONS.mensagem();
  };
  const aoAbrirMenu = (aberto) => {
    // pede permissão de aviso na área de trabalho num clique da pessoa (o navegador exige)
    if (aberto && typeof window.Notification === 'function' && Notification.permission === 'default') Notification.requestPermission().catch(() => {});
  };

  return (
    <DropdownMenu onOpenChange={aoAbrirMenu}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="wa-alertas-sino"
          title={total ? `Atendimento: ${total} aviso(s)` : 'Atendimento WhatsApp'}>
          <MessageCircle className={`h-5 w-5 ${transferencias.length ? 'text-emerald-600' : ''}`} />
          {total > 0 && (
            <Badge className={`absolute -top-1 -right-1 h-5 min-w-[1.25rem] px-1 flex items-center justify-center text-xs text-white ${transferencias.length ? 'bg-emerald-600 animate-pulse' : 'bg-red-500'}`}
              data-testid="wa-alertas-count">
              {total > 9 ? '9+' : total}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[30rem] overflow-auto" data-testid="wa-alertas-lista">
        <DropdownMenuLabel className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-emerald-600" />
          <span className="flex-1">Atendimento</span>
          <button onClick={alternarSom} className="text-xs font-normal text-muted-foreground hover:text-foreground flex items-center gap-1"
            title={som ? 'Desligar o som dos avisos' : 'Ligar o som dos avisos'} data-testid="wa-alertas-som">
            {som ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}{som ? 'som ligado' : 'som desligado'}
          </button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {total === 0 && <div className="p-4 text-center text-muted-foreground text-sm">Nenhuma mensagem nova</div>}

        {transferencias.map(t => (
          <DropdownMenuItem key={t.id} className="flex items-start gap-2 p-3 cursor-pointer" onClick={() => abrir(t.jid, t.id)} data-testid={`wa-alerta-transf-${t.id}`}>
            <ArrowRightLeft className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{t.cliente || 'Atendimento'} foi transferido para você</p>
              <p className="text-xs text-muted-foreground">por {t.de || '—'} · {hora(t.at)}</p>
            </div>
          </DropdownMenuItem>
        ))}

        {mencoes > 0 && (
          <DropdownMenuItem className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => navigate('/whatsapp')} data-testid="wa-alerta-mencoes">
            <AtSign className="w-4 h-4 text-violet-600 shrink-0" />
            <p className="text-sm flex-1">{mencoes === 1 ? 'Você foi mencionado numa nota' : `Você foi mencionado em ${mencoes} notas`}</p>
          </DropdownMenuItem>
        )}

        {conversas.map(c => (
          <DropdownMenuItem key={c.jid} className="flex items-start gap-2 p-3 cursor-pointer" onClick={() => abrir(c.jid)} data-testid={`wa-alerta-conv-${c.numero || c.jid}`}>
            {c.atendimento_status === 'fila' ? <Clock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" /> : <MessageCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <p className="text-sm font-medium truncate flex-1">{nomeConv(c)}</p>
                <span className="text-[11px] text-muted-foreground shrink-0">{hora(c.ultima_data)}</span>
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {c.atendimento_status === 'fila' ? `Na fila${c.setor_nome ? ` · ${c.setor_nome}` : ''} · ` : ''}{semMarcas(c.ultima_mensagem || '')}
              </p>
            </div>
            <span className="bg-emerald-500 text-white text-[11px] font-semibold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center shrink-0">{c.nao_lidas}</span>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem className="justify-center text-xs text-primary cursor-pointer" onClick={() => navigate('/whatsapp')} data-testid="wa-alertas-abrir">
          Abrir o atendimento
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
